import React, { useEffect, useMemo, useState } from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFormik } from 'formik';
import { useMutation, useQuery } from 'react-query';
import withAuth from '../../../utils/hocs/withAuth';
import withSidebar from '../../../utils/hocs/withSidebar';
import SCOPE from '../../../utils/gate/scope';
import withPermission from '../../../utils/hocs/withPermission';
import useNotification from '../../../utils/hooks/useNotification';
import buildTabs from '../../../utils/tools/buildTabs';
import accommodationMarketingSchema from '../../../validations/products/accommodation/marketingSchema';
import useForm from '../../../utils/hooks/useForm';

// Services
import useAccommodationService from '../../../services/accommodationService';

// Icons
import { ReactComponent as FileIcon } from '../../../assets/icons/file.svg';
import { ReactComponent as ToolsIcon } from '../../../assets/icons/tools.svg';
import { ReactComponent as PictureIcon } from '../../../assets/icons/picture.svg';

// Components
import PageWrapper from '../../../components/layout/PageWrapper/PageWrapper';
import General from '../../../components/views/establishment/Products/Accommodations/Sections/General/General';
import Features from '../../../components/views/establishment/Products/Accommodations/Sections/Features/Features';
import LayoutWithTabs from '../../../components/layout/LayoutWithTabs/LayoutWithTabs';
import Medias from '../../../components/views/establishment/Products/Accommodations/Sections/Medias/Medias';
import SkeletonLoader from '../../../components/molecules/SkeletonLoader/SkeletonLoader';
import Button from '../../../components/atoms/Button/Button';

function AccommodationMarketing() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const { displayNotification } = useNotification();
  const { handleScrollToError } = useForm();

  // URL query params
  const [searchParams] = useSearchParams();
  const accommodationId = useMemo(() => searchParams.get('id'), [searchParams]);
  useEffect(() => { if (!accommodationId) navigate('/products/accommodations'); }, []);

  const [accommodation, setAccommodation] = useState(null);
  const [initialValues, setInitialValues] = useState({
    name: '',
    type: null,
    features: [],
    capacity: '',
    size: '',
    spec: null,
    checkInFromHour: null,
    checkOutUntilHour: null,
    isBreakfastIncluded: false,
    mainImageMedia: null,
    sliderImageMedias: null,
  });

  const { getAccommodation, putAccommodation } = useAccommodationService();

  const getSpecValueFromBool = ({ isAnAdultMandatory, isForChildrenOnly }) => {
    if (isAnAdultMandatory) {
      return { value: 'isAnAdultMandatory', label: t('accommodations.marketing.form.isAnAdultMandatory') };
    }
    if (isForChildrenOnly) {
      return { value: 'isForChildrenOnly', label: t('accommodations.marketing.form.isForChildrenOnly') };
    }
    return null;
  };

  const getFormattedAccommodation = (values) => ({
    name: values.name,
    type: values.type ? { value: values.type, label: t(`accommodations.types.${values.type}`) } : null,
    features: values.features?.length
      ? values.features.map((feature) => (
        { value: feature, label: t(`accommodations.features.${feature}`) }
      ))
      : [],
    capacity: values.capacity,
    size: values.size,
    spec: getSpecValueFromBool({
      isAnAdultMandatory: values.isAnAdultMandatory,
      isForChildrenOnly: values.isForChildrenOnly,
    }),
    checkInFromHour: values.checkInFromHour ? (
      { label: `${values.checkInFromHour}:00`, value: values.checkInFromHour }
    ) : '',
    checkOutUntilHour: values.checkOutUntilHour ? (
      { label: `${values.checkOutUntilHour}:00`, value: values.checkOutUntilHour }
    ) : '',
    isBreakfastIncluded: values.isBreakfastIncluded,
    mainImageMedia: values.mainImageMedia,
    sliderImageMedias: values.sliderImageMedias,
  });

  const { isFetching } = useQuery('getAccommodation', () => getAccommodation(accommodationId), {
    enabled: !!accommodationId,
    onSuccess: (res) => {
      if (res?.data) {
        setAccommodation(res.data);
        setInitialValues(getFormattedAccommodation(res.data));
      }
    },
    onError: () => {
      navigate('/products/accommodations');
    },
  });

  const { isLoading: isPutAccommodationLoading, mutate: putAccommodationMutate } = useMutation(
    (values) => putAccommodation({ accommodationId, accommodation: values }),
    {
      onSuccess: (res) => {
        displayNotification({
          message: t('common.successfullySaved'),
        });

        if (res?.data) {
          setInitialValues(getFormattedAccommodation(res.data));
        }
      },
      onError: (error) => {
        displayNotification({
          message: t(error?.response?.data?.code
            ? `code_errors.${error.response.data.code}`
            : 'common.saveError'),
          color: 'error',
        });
      },
    },
  );

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    onSubmit: (values) => {
      if (values) {
        putAccommodationMutate({
          name: values.name,
          type: values.type?.value,
          features: values.features?.map((feature) => feature.value) || [],
          capacity: values.capacity,
          size: values.size,
          isAnAdultMandatory: values.spec?.value === 'isAnAdultMandatory' || false,
          isForChildrenOnly: values.spec?.value === 'isForChildrenOnly' || false,
          checkInFromHour: values.checkInFromHour?.value,
          checkOutUntilHour: values.checkOutUntilHour?.value,
          isBreakfastIncluded: values.isBreakfastIncluded,
          mainImageMediaId: values.mainImageMedia?.id,
          sliderImageMediaIds: values.sliderImageMedias?.map((media) => media.id),
        });
      }
    },
    validationSchema: accommodationMarketingSchema,
    validateOnBlur: true,
  });

  const getTabComponent = (TabComponent, props) => (
    <TabComponent
      formik={{
        values: formik.values,
        handleChange: formik.handleChange,
        handleBlur: formik.handleBlur,
        setFieldValue: formik.setFieldValue,
        setFieldTouched: formik.setFieldTouched,
        touched: formik.touched,
        errors: formik.errors,
      }}
      {...props}
    />
  );

  const tabs = buildTabs([
    {
      label: 'accommodations.marketing.sections.general',
      component: getTabComponent(General, {}),
      icon: <FileIcon />,
    },
    {
      label: 'accommodations.marketing.sections.features',
      component: getTabComponent(Features, {}),
      icon: <ToolsIcon />,
    },
    {
      label: 'accommodations.marketing.sections.medias',
      component: getTabComponent(Medias, {}),
      icon: <PictureIcon />,
    },
  ]);

  return (
    <PageWrapper>
      <LayoutWithTabs
        tabs={tabs}
        breadcrumb={isFetching ? null : [
          { label: t('accommodations.title'), href: '/products/accommodations' },
        ]}
        title={accommodation?.name}
        isFetching={isFetching}
        skeletonLoader={(
          <>
            <SkeletonLoader className="m-b-30" height={30} />

            {tabs?.map((_, index) => (
              <React.Fragment key={index}>
                <SkeletonLoader className="m-b-20" height={50} />
                <SkeletonLoader className="m-b-50" height={200} />
              </React.Fragment>
            ))}
          </>
        )}
        moreContent={accommodation && !isFetching ? (
          <div className="d-flex a-center j-end sticky-actions p-r-30 mobile-p-r-0">
            <Button
              full={isMobile}
              onClick={() => {
                formik.submitForm().then(() => handleScrollToError(formik.errors));
              }}
              isLoading={isPutAccommodationLoading}
              disabled={!formik.dirty}
            >
              {t('common.saveModifications')}
            </Button>
          </div>
        ) : null}
      />
    </PageWrapper>
  );
}

export default withAuth(
  withPermission(
    withSidebar(AccommodationMarketing, { withTabsBar: true }),
    {
      scopes: [SCOPE.update_ownEst_accommodation, SCOPE.create_ownEst_accommodation],
    },
  ),
);
